import { wrapReducer } from "@client/content/lib/helper";
import { type FailureActionData } from "@client/lib/actions";
import { Status } from "@client/types";
import { type ReduxAction } from "@client/types/ReduxAction";
import { isNull } from "lodash";
import {
  Actions,
  type InitSuccessActionData,
  type LoginSuccessActionData,
  type ResendLinkSuccessActionData,
  type SetFieldErrorsActionData,
  type SetModeActionData
} from "./actions";
import { type LoginForm } from "./types";

const reducer = (state: LoginForm.State, action: ReduxAction<any>): LoginForm.State => {
  switch (action.type) {
    case Actions.InitBegin: {
      return {
        ...state,
        status: Status.Loading
      };
    }
    case Actions.InitFailure: {
      return {
        ...state,
        status: Status.Failed
      };
    }
    case Actions.InitSuccess: {
      const { canLogin, isReset } = action.data as InitSuccessActionData;
      return {
        ...state,
        canLogin,
        mode: isNull(isReset) ? "login" : isReset ? "resetPassword" : "expiredLink",
        status: Status.Ready
      };
    }

    case Actions.LoginSuccess: {
      const { isAdmin, loginUrl } = action.data as LoginSuccessActionData;
      return {
        ...state,
        error: null,
        isAdmin,
        loginUrl,
        status: Status.Ready
      };
    }

    case Actions.LoginBegin:
    case Actions.ResetPasswordBegin:
    case Actions.SendResetLinkBegin:
    case Actions.ResendLinkBegin: {
      return {
        ...state,
        error: null,
        fieldErrors: {},
        status: Status.Saving
      };
    }
    case Actions.LoginFailure:
    case Actions.ResetPasswordFailure:
    case Actions.SendResetLinkFailure:
    case Actions.ResendLinkFailure: {
      const { error } = action.data as FailureActionData;
      return {
        ...state,
        error,
        status: Status.Failed
      };
    }
    case Actions.SendResetLinkSuccess: {
      return {
        ...state,
        isLinkSent: true,
        status: Status.Ready
      };
    }

    case Actions.ResendLinkSuccess: {
      const data = action.data as ResendLinkSuccessActionData;
      return {
        ...state,
        isLinkSent: data.isSuccessful ? true : null,
        mode: data.isSuccessful ? "sendResetLink" : "login",
        status: Status.Ready
      };
    }

    case Actions.ResetPasswordSuccess: {
      return {
        ...state,
        status: Status.Ready
      };
    }

    case Actions.SetFieldErrors: {
      const { fieldErrors } = action.data as SetFieldErrorsActionData;
      return {
        ...state,
        fieldErrors
      };
    }

    case Actions.SetMode: {
      const { mode } = action.data as SetModeActionData;
      return {
        ...state,
        mode,
        isLinkSent: null
      };
    }

    default:
      return state;
  }
};

export const defaultState: LoginForm.State = {
  alert: null,
  canLogin: false,
  error: null,
  fieldErrors: {},
  id: "",
  isLinkSent: null,
  isAdmin: false,
  loginUrl: null,
  status: Status.None,
  mode: "login"
};
export const previewState: LoginForm.State = {
  ...defaultState,
  status: Status.Ready
};
export default wrapReducer<LoginForm.State>({ defaultState, reducer, previewState });
